import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  FileType,
  Image as ImageIcon,
  Table as TableIcon,
  Download,
  Loader2,
  CheckCircle2,
  Trash2,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import WizardStepper from "@/components/wizard/WizardStepper";
import TutorialDeepLink from "@/components/TutorialDeepLink";
import DebtVitaInclusionToggle from "@/components/debt/DebtVitaInclusionToggle";
import ReportTemplate from "@/components/report/ReportTemplate";
import PosterTemplate from "@/components/report/PosterTemplate";
import { getProject } from "@/lib/profiles";
import { getLatestAnalysis } from "@/lib/analyses";
import { pingFeature } from "@/lib/admin-api";
import {
  REPORT_FORMATS,
  buildReportFilename,
  listReports,
  logReport,
  deleteReport,
} from "@/lib/reports";
import { generatePdf, generatePosterPng } from "@/lib/report-generators/pdf";
import { generateExcel } from "@/lib/report-generators/excel";
import { generateWord } from "@/lib/report-generators/word";

const FORMAT_ICONS = {
  pdf: FileText,
  docx: FileType,
  xlsx: TableIcon,
  png: ImageIcon,
};

const FORMAT_ACCENTS = {
  pdf: "#C0392B",
  docx: "#1A3C5E",
  xlsx: "#1F7A3E",
  png: "#C9A84C",
};

export default function WizardReport() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const reportRootRef = useRef(null);
  const posterRef = useRef(null);

  const [project, setProject] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, an, list] = await Promise.all([
        getProject(projectId),
        getLatestAnalysis(projectId),
        listReports(projectId),
      ]);
      if (!proj) {
        toast.error("Projet introuvable.");
        navigate("/app/dashboard", { replace: true });
        return;
      }
      setProject(proj);
      setAnalysis(an);
      setReports(list || []);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Impossible de charger la page rapport."
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const canGenerate = useMemo(() => !!analysis && !generating, [
    analysis,
    generating,
  ]);

  const handleGenerate = async (fmtKey) => {
    // eslint-disable-next-line no-console
    console.log("[report] handleGenerate click", fmtKey);
    if (!analysis) {
      toast.error("Aucune analyse disponible.");
      return;
    }
    const meta = REPORT_FORMATS.find((f) => f.key === fmtKey);
    if (!meta) return;
    const filename = buildReportFilename({
      country: project?.country,
      fiscalYear: project?.fiscal_year,
      extension: meta.extension,
    });
    setGenerating(fmtKey);
    try {
      let sizeBytes = 0;
      if (fmtKey === "pdf") {
        sizeBytes = await generatePdf({
          rootNode: reportRootRef.current,
          filename,
        });
      } else if (fmtKey === "xlsx") {
        sizeBytes = await generateExcel({ analysis, filename });
      } else if (fmtKey === "docx") {
        sizeBytes = await generateWord({ project, analysis, filename });
      } else if (fmtKey === "png") {
        const posterNode =
          posterRef.current?.querySelector("#vp-poster-template") ||
          posterRef.current;
        sizeBytes = await generatePosterPng({
          posterNode,
          filename,
        });
      }
      toast.success(`Rapport ${meta.label} généré : ${filename}`);
      pingFeature(`report.export.${fmtKey}`, {
        project_id: projectId,
        metadata: { format: fmtKey, size_bytes: sizeBytes || null },
      });
      try {
        await logReport(projectId, {
          analysis_id: analysis.id,
          format: fmtKey,
          filename,
          size_bytes: sizeBytes || null,
        });
        const fresh = await listReports(projectId);
        setReports(fresh || []);
      } catch (logErr) {
        // Non-blocking: generation succeeded; we just failed to persist metadata
        // eslint-disable-next-line no-console
        console.warn("logReport failed:", logErr);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[report] generation failed for", fmtKey, e);
      toast.error(
        e?.message || "La génération du rapport a échoué. Réessayez."
      );
    } finally {
      setGenerating(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet enregistrement de rapport ?")) return;
    try {
      await deleteReport(id);
      setReports((prev) => prev.filter((r) => r.id !== id));
      toast.success("Enregistrement supprimé.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible.");
    }
  };

  if (loading) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center"
        data-testid="wizard-report-loading"
      >
        <div className="text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wizard-report-page">
      {/* Header */}
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Nouveau projet · Étape 7/7
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
          Générer le rapport
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          {project?.name}
          {project?.fiscal_year && ` · Exercice ${project.fiscal_year}`} —
          Exportez votre analyse vitaliste dans le format de votre choix.
          Génération 100% côté client : aucun appel LLM.
        </p>
      </div>

      <WizardStepper current={7} />
      <TutorialDeepLink
        chapter={8}
        section="section-8-1"
        id="wizard-step-7-report"
        label="Choisir le bon format d'export et personnaliser le rapport"
        duration={6}
      />

      {!analysis ? (
        <Card className="border-dashed" data-testid="no-analysis-card">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Info className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Analyse requise</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              Lancez l'analyse vitaliste (étape 3) avant de générer un rapport.
            </p>
            <Button
              onClick={() =>
                navigate(`/app/projects/${projectId}/wizard/confirm`)
              }
              data-testid="go-confirm-btn"
            >
              Aller à l'étape 3
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Format grid */}
          <div
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            data-testid="report-format-grid"
          >
            {REPORT_FORMATS.map((fmt) => {
              const Icon = FORMAT_ICONS[fmt.key];
              const accent = FORMAT_ACCENTS[fmt.key];
              const isGen = generating === fmt.key;
              const previewName = buildReportFilename({
                country: project?.country,
                fiscalYear: project?.fiscal_year,
                extension: fmt.extension,
              });
              return (
                <Card
                  key={fmt.key}
                  className="transition-shadow hover:shadow-md"
                  style={{ borderLeft: `4px solid ${accent}` }}
                  data-testid={`report-format-card-${fmt.key}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Icon className="h-5 w-5" style={{ color: accent }} />
                      {fmt.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      {fmt.description}
                    </p>
                    <div className="truncate rounded border border-dashed border-border bg-muted/30 px-2 py-1.5 font-mono text-[11px]">
                      {previewName}
                    </div>
                    <Button
                      onClick={() => handleGenerate(fmt.key)}
                      disabled={!canGenerate}
                      className="w-full"
                      data-testid={`generate-${fmt.key}-btn`}
                      style={{ backgroundColor: accent, color: "#fff" }}
                    >
                      {isGen ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Génération…
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Générer {fmt.label}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* DEBT-VITA™ inclusion section */}
          <DebtVitaInclusionToggle projectId={projectId} project={project} />

          {/* Report history */}
          <Card data-testid="reports-history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                Historique des rapports générés
                <span className="ml-2 rounded bg-muted px-2 py-0.5 font-mono text-xs">
                  {reports.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reports.length === 0 ? (
                <div
                  className="rounded-md border border-dashed border-border bg-muted/20 p-5 text-center text-sm text-muted-foreground"
                  data-testid="reports-history-empty"
                >
                  Aucun rapport généré pour ce projet pour l'instant.
                </div>
              ) : (
                <ul
                  className="space-y-2"
                  data-testid="reports-history-list"
                >
                  {reports.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between rounded-md border border-border bg-card p-3"
                      data-testid={`report-history-row-${r.id}`}
                    >
                      <div className="min-w-0">
                        <div
                          className="truncate text-sm font-semibold"
                          title={r.filename}
                        >
                          {r.filename}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {r.format?.toUpperCase()} ·{" "}
                          {r.created_at
                            ? new Date(r.created_at).toLocaleString("fr-FR")
                            : "—"}
                          {r.size_bytes
                            ? ` · ${(r.size_bytes / 1024).toFixed(0)} Ko`
                            : ""}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(r.id)}
                        data-testid={`report-history-delete-${r.id}`}
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Footer nav */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button
          variant="outline"
          onClick={() =>
            navigate(`/app/projects/${projectId}/wizard/judgment`)
          }
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Étape précédente
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/app/analyses")}
          data-testid="goto-analyses-btn"
        >
          Retour à mes analyses
        </Button>
      </div>

      {/* Off-screen report + poster templates (rendered only when analysis exists) */}
      {analysis && (
        <div ref={reportRootRef}>
          <ReportTemplate project={project} analysis={analysis} />
        </div>
      )}
      {analysis && (
        <div ref={posterRef}>
          <PosterTemplate project={project} analysis={analysis} />
        </div>
      )}
    </div>
  );
}
